import { IToken } from 'app/models/Notification/Notification';
import { PayPackageDetailsValuesSubCategory } from './enum';
import moment from 'moment';
import { DocStatus, PlacementDocument, PlacementDocumentType } from 'store/redux-store/placement-documents/types';
import { convertToPercentage, formatNumber, getNumberMatchStatusAsYesNo } from './Components/PayPackage/helper';
import {
  IPlacementMarginDetailsResponseScenarioSplitItem,
  IPlacementMarginDetailsResponseScenario,
  IPlacementMarginDetailsResponse,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.model';

const baseUrl = globalThis?.app?.env?.REACT_APP_BASE_URL;

export interface IEmailVariantData {
  subject: string;
  body: string;
  substitutions: IToken[];
}

interface IMarginToolSpecialty {
  specialtyAbbr?: string;
}

export interface IMarginToolSkillset {
  disciplineAbbr?: string;
  specialties?: IMarginToolSpecialty[];
}

export function getLatestSentDateForClientConfirmation(docs: PlacementDocument[]) {
  const clientConfirmations = docs?.filter(
    doc =>
      doc?.document?.toLowerCase() === PlacementDocumentType.clientConfirmation.toLowerCase() &&
      doc?.status === DocStatus.Sent,
  );

  if (clientConfirmations?.length === 0) {
    return null;
  }

  const latestDocument = clientConfirmations.reduce((latest, current) => {
    return new Date(latest.sent) > new Date(current.sent) ? latest : current;
  });

  return latestDocument?.sent;
}

export const formatSkillsets = (skillsets: IMarginToolSkillset[]) => {
  return skillsets
    ?.map(skillset =>
      skillset?.specialties?.map(specialty => `${skillset.disciplineAbbr}-${specialty.specialtyAbbr}`).join('/'),
    )
    .join(' / ');
};

export const getProcessedSplits = (
  splits: IPlacementMarginDetailsResponseScenarioSplitItem[],
  billRateMod: any,
  headerData: IPlacementMarginDetailsResponseScenario,
) => {
  let modData;
  if (!!billRateMod) {
    modData = {
      approval_details: `(Approved:  ${
        moment(billRateMod?.approvalDate)?.isValid() ? moment(billRateMod?.approvalDate)?.format('MM/DD/YYYY') : ''
      } ${
        billRateMod?.approvalDate && (billRateMod?.approvedByFirstName || billRateMod?.approvedByLastName) ? 'by ' : ''
      }${billRateMod?.approvedByFirstName ?? ''} ${billRateMod?.approvedByLastName ?? ''})`,

      bill_rate_current_mod: billRateMod?.regularRate ? `${formatNumber(billRateMod.regularRate, 2)}` : '',
      additional_overtime_rate_current_mod: billRateMod?.overtimeRate
        ? `${formatNumber(billRateMod.overtimeRate, 2)}`
        : '',
      overtime_factor_current_mod: billRateMod?.overtimeFactor ? `${formatNumber(billRateMod.overtimeFactor, 2)}` : '',
      additional_call_back_rate_current_mod: billRateMod?.overtimeRate
        ? `${formatNumber(billRateMod.overtimeRate, 2)}`
        : '',
      call_back_factor_current_mod: billRateMod?.callBackFactor ? `${formatNumber(billRateMod.callBackFactor, 2)}` : '',
      additional_double_time_rate_current_mod: billRateMod?.doubleTimeRate
        ? `${formatNumber(billRateMod.doubleTimeRate, 2)}`
        : '',
      double_time_factor_current_mod: billRateMod?.doubletimeFactor
        ? `${formatNumber(billRateMod.doubletimeFactor, 2)}`
        : '',
      additional_holiday_rate_current_mod: billRateMod?.holidayRate
        ? `${formatNumber(billRateMod.holidayRate, 2)}`
        : '',
      holiday_factor_current_mod: billRateMod?.holidayFactor ? `${formatNumber(billRateMod.holidayFactor, 2)}` : '',
      on_call_rate_current_mod: billRateMod?.onCallRate ? `${formatNumber(billRateMod.onCallRate, 2)}` : '',
      charge_rate_current_mod: billRateMod?.chargePremium ? `${formatNumber(billRateMod.chargePremium, 2)}` : '',
      amount_per_mile_current_mod: billRateMod?.amountPerMile ? `${formatNumber(billRateMod.amountPerMile, 3)}` : '',
      guaranteed_hours_current_mod: billRateMod?.hoursPerPayCycle
        ? `${formatNumber(billRateMod.hoursPerPayCycle, 2)}`
        : '',
      preceptor_rate_current_mod: billRateMod?.preceptorRate ? `${formatNumber(billRateMod.preceptorRate, 2)}` : '',
      orientation_rate_current_mod: billRateMod?.orientationRate
        ? `${formatNumber(billRateMod.orientationRate, 2)}`
        : '',
      orientation_factor_current_mod: billRateMod?.orientationFactor
        ? `${formatNumber(billRateMod.orientationFactor, 2)}`
        : '',
    };
  }
  const additionalPremiumPay = headerData?.assignment?.payPackageDetailsValues?.find(
    payPackage => payPackage?.subCategory === PayPackageDetailsValuesSubCategory.additionalPremiumPay,
  )?.pickListDescription;

  const splitData = splits?.map((split, splitIndex: number) => {
    const orientation = split?.isVoidOrientationHours
      ? `${split?.orientationHours && split?.orientationHours > 0 ? formatNumber(split?.orientationHours, 2) : ''}` +
        (split?.orientationHours ? ' - ' : '') +
        `&lt;Voided&gt;` +
        (split?.orientationOverrideDate ? ` - ${split.orientationOverrideDate}` : '') +
        (split?.orientationOverrideFirst || split?.orientationOverrideLast ? ' by ' : '') +
        `${split?.orientationOverrideFirst ?? ''} ${split?.orientationOverrideLast ?? ''}`
      : `${split?.orientationHours && split?.orientationHours > 0 ? formatNumber(split?.orientationHours, 2) : ''}`;

    const marginValue = {
      split_index: `${splitIndex + 1}`,
      split_start_date: split?.effectiveStartDate ? moment(split?.effectiveStartDate)?.format('MM/DD/YYYY') : '',
      split_end_date: split?.effectiveEndDate ? moment(split?.effectiveEndDate)?.format('MM/DD/YYYY') : '',

      bill_rate_margin_value: split?.billRate ? `${formatNumber(split.billRate, 2)}` : '',
      additional_overtime_rate_margin_value: split?.overtime ? `${formatNumber(split.overtime, 2)}` : '',
      overtime_factor_margin_value: split?.billOvertimeFactor ? `${formatNumber(split.billOvertimeFactor, 2)}` : '',
      additional_call_back_rate_margin_value: split?.callback ? `${formatNumber(split.callback, 2)}` : '',
      call_back_factor_margin_value: split?.billCallBackFactor ? `${formatNumber(split.billCallBackFactor, 2)}` : '',
      additional_double_time_rate_margin_value: split?.billDoubletimeRate
        ? `${formatNumber(split.billDoubletimeRate, 2)}`
        : '',
      double_time_factor_margin_value: split?.billDoubletimeFactor
        ? `${formatNumber(split.billDoubletimeFactor, 2)}`
        : '',
      additional_holiday_rate_margin_value: split?.holiday ? `${formatNumber(split.holiday, 2)}` : '',
      holiday_factor_margin_value: split?.billHolidayFactor ? `${formatNumber(split.billHolidayFactor, 2)}` : '',
      on_call_rate_margin_value: split?.billOnCallRate ? `${formatNumber(split.billOnCallRate, 2)}` : '',
      charge_rate_margin_value: split?.billCharge ? `${formatNumber(split.billCharge, 2)}` : '',
      amount_per_mile_margin_value: split?.billAmountPerMile ? `${formatNumber(split.billAmountPerMile, 3)}` : '',
      guaranteed_hours_margin_value: split?.billGuaranteedHours ? `${formatNumber(split.billGuaranteedHours, 2)}` : '',
      preceptor_rate_margin_value: split?.billPreceptor ? `${formatNumber(split.billPreceptor, 2)}` : '',
      orientation_rate_margin_value: split?.orientationRate ? `${formatNumber(split.orientationRate, 2)}` : '',
      orientation_factor_margin_value: split?.orientationFactor ? `${formatNumber(split.orientationFactor, 2)}` : '',

      orientation_hours_value: orientation,

      pay_rate_value: split?.payRate ? `${formatNumber(split.payRate, 2)}` : '',
      overtime_factor_value: split?.payOvertimeFactor ? `${formatNumber(split.payOvertimeFactor, 2)}` : '',
      call_back_factor_value: split?.payCallBackFactor ? `${formatNumber(split.payCallBackFactor, 2)}` : '',
      holiday_factor_value: split?.payHolidayFactor ? `${formatNumber(split.payHolidayFactor, 2)}` : '',
      double_time_factor_value: split?.payDoubletimeFactor ? `${formatNumber(split.payDoubletimeFactor, 2)}` : '',
      on_call_rate_value: split?.payOnCallRate ? `${formatNumber(split.payOnCallRate, 2)}` : '',
      amount_per_mile_value: split?.payAmountPerMile ? `${formatNumber(split.payAmountPerMile, 3)}` : '',
      guaranteed_hours_value: split?.payGuaranteedHours ? `${formatNumber(split.payGuaranteedHours, 2)}` : '',
      charge_value: split?.payCharge ? `${formatNumber(split.payCharge, 2)}` : '',
      preceptor_value: split?.payPreceptor ? `${formatNumber(split.payPreceptor, 2)}` : '',
      additional_premium_pay_value:
        additionalPremiumPay && !isNaN(parseFloat(additionalPremiumPay))
          ? `${formatNumber(additionalPremiumPay, 2)}`
          : '',
    };

    const matchValue = {
      bill_rate_match: getNumberMatchStatusAsYesNo(modData?.bill_rate_current_mod, marginValue?.bill_rate_margin_value),
      additional_overtime_rate_match: getNumberMatchStatusAsYesNo(
        modData?.additional_overtime_rate_current_mod,
        marginValue?.additional_overtime_rate_margin_value,
      ),
      overtime_factor_match: getNumberMatchStatusAsYesNo(
        modData?.overtime_factor_current_mod,
        marginValue?.overtime_factor_margin_value,
      ),
      additional_call_back_rate_match: getNumberMatchStatusAsYesNo(
        modData?.additional_call_back_rate_current_mod,
        marginValue?.additional_call_back_rate_margin_value,
      ),
      call_back_factor_match: getNumberMatchStatusAsYesNo(
        modData?.call_back_factor_current_mod,
        marginValue?.call_back_factor_margin_value,
      ),
      additional_double_time_rate_match: getNumberMatchStatusAsYesNo(
        modData?.additional_double_time_rate_current_mod,
        marginValue?.additional_double_time_rate_margin_value,
      ),
      double_time_factor_match: getNumberMatchStatusAsYesNo(
        modData?.double_time_factor_current_mod,
        marginValue?.double_time_factor_margin_value,
      ),
      additional_holiday_rate_match: getNumberMatchStatusAsYesNo(
        modData?.additional_holiday_rate_current_mod,
        marginValue?.additional_holiday_rate_margin_value,
      ),
      holiday_factor_match: getNumberMatchStatusAsYesNo(
        modData?.holiday_factor_current_mod,
        marginValue?.holiday_factor_margin_value,
      ),
      on_call_rate_match: getNumberMatchStatusAsYesNo(
        modData?.on_call_rate_current_mod,
        marginValue?.on_call_rate_margin_value,
      ),
      charge_rate_match: getNumberMatchStatusAsYesNo(
        modData?.charge_rate_current_mod,
        marginValue?.charge_rate_margin_value,
      ),
      amount_per_mile_match: getNumberMatchStatusAsYesNo(
        modData?.amount_per_mile_current_mod,
        marginValue?.amount_per_mile_margin_value,
      ),
      guaranteed_hours_match: getNumberMatchStatusAsYesNo(
        modData?.guaranteed_hours_current_mod,
        marginValue?.guaranteed_hours_margin_value,
      ),
      preceptor_rate_match: getNumberMatchStatusAsYesNo(
        modData?.preceptor_rate_current_mod,
        marginValue?.preceptor_rate_margin_value,
      ),
      orientation_rate_match: getNumberMatchStatusAsYesNo(
        modData?.orientation_rate_current_mod,
        marginValue?.orientation_rate_margin_value,
      ),
      orientation_factor_match: getNumberMatchStatusAsYesNo(
        modData?.orientation_factor_current_mod,
        marginValue?.orientation_factor_margin_value,
      ),
    };

    return {
      ...marginValue,
      ...modData,
      ...matchValue,
    };
  });
  return splitData;
};

export const getApprovalVariantEmailSubstitutions = (
  currentMarginData: IPlacementMarginDetailsResponse,
  extensionOfPriorPlacementId: number | string,
  placementHeaderData: any,
  orderData: any,
  candidateName: string,
  recruiterName: string,
  recruiterEmail: string,
  accountManagerName: string,
  accountManagerEmail: string,
  formattedLatestSentDate: string,
  headerData: IPlacementMarginDetailsResponseScenario,
  bookingData: IPlacementMarginDetailsResponse,
) => {
  const extension = currentMarginData?.isScenarioExtention
    ? `Yes (<a href='${baseUrl}/placement/${extensionOfPriorPlacementId}'> PlacementID ${extensionOfPriorPlacementId}</a> )`
    : 'No';

  const formattedSkillsets = formatSkillsets(placementHeaderData?.skillsets);

  const formattedAddress = `${bookingData?.facility?.facilityAddress1 || ''}${
    bookingData?.facility?.facilityAddress2 ? ` ${bookingData?.facility?.facilityAddress2}` : ''
  }${bookingData?.facility?.facilityCity ? `, ${bookingData?.facility?.facilityCity}` : ''}${
    bookingData?.facility?.facilityCounty ? `, ${bookingData?.facility?.facilityCounty}` : ''
  }${bookingData?.facility?.facilityState ? `, ${bookingData?.facility?.facilityState}` : ''}${
    bookingData?.facility?.facilityZip ? `, ${bookingData?.facility?.facilityZip}` : ''
  }`
    .trim()
    .replace(/^,|,$/g, '');

  const bookingPeriod =
    `${
      moment(bookingData?.bookingPeriodStartDate)?.isValid()
        ? moment(bookingData?.bookingPeriodStartDate)?.format('MM/DD/YYYY')
        : ''
    }` +
    (moment(bookingData?.bookingPeriodStartDate)?.isValid() && moment(bookingData?.bookingPeriodEndDate)?.isValid()
      ? ' - '
      : '') +
    `${
      moment(bookingData?.bookingPeriodEndDate)?.isValid()
        ? moment(bookingData?.bookingPeriodEndDate)?.format('MM/DD/YYYY')
        : ''
    }` +
    (bookingData?.bookingPeriodDuration ? ` (${bookingData?.bookingPeriodDuration})` : '');

  const placementDates =
    `${
      moment(placementHeaderData?.placementStartDate)?.isValid()
        ? moment(placementHeaderData.placementStartDate)?.format('MM/DD/YYYY')
        : ''
    }` +
    (moment(placementHeaderData?.placementStartDate)?.isValid() &&
    moment(placementHeaderData?.placementEndDate)?.isValid()
      ? ' - '
      : '') +
    `${
      moment(placementHeaderData?.placementEndDate)?.isValid()
        ? moment(placementHeaderData.placementEndDate)?.format('MM/DD/YYYY')
        : ''
    }` +
    (placementHeaderData?.duration ? ` (${placementHeaderData?.duration})` : '');

  const substitutions: IToken[] = [];

  const substitutionData = [
    { name: 'Signature', input: '{{signature}}' },
    { name: 'Notes', input: '{{notes}}', value: `${headerData?.notes ?? ''}` },
    {
      name: 'Margin Url',
      input: '{{margin_url}}',
      value: `${baseUrl}/margin/placement/${headerData?.placementId}`,
    },
    { name: 'Mods Url', input: '{{mods_url}}', value: `${baseUrl}/placement/${headerData?.placementId}?tab=3` },
    {
      name: 'Confirmations Url',
      input: '{{confirmations_url}}',
      value: `${baseUrl}/placement/${headerData?.placementId}?tab=4&view=client-confirmation-editor&default_selection=draft`,
    },
    {
      name: 'Gross Margin Value',
      input: '{{gross_margin_value}}',
      value: headerData?.grossMargin ? `${convertToPercentage(headerData?.grossMargin)}` : '',
    },
    {
      name: 'Gross Profit Value',
      input: '{{gross_profit_value}}',
      value: headerData?.grossProfit ? `$${headerData.grossProfit.toFixed(2)}` : '',
    },
    {
      name: 'Net Contributed Margin Value',
      input: '{{net_contributed_margin_value}}',
      value: headerData?.negotiatedContributionMargin
        ? `$${parseFloat(headerData.negotiatedContributionMargin).toFixed(2)}`
        : '',
    },
    { name: 'Placement ID', input: '{{placement_id}}', value: `${headerData?.placementId}` },
    {
      name: 'Placement Link',
      input: '{{placement_url}}',
      value: `${baseUrl}/placement/${headerData?.placementId}`,
    },
    {
      name: 'VMS',
      input: '{{vms}}',
      value:
        `${placementHeaderData?.vmsReqNo ?? ''}` +
        (placementHeaderData?.vmsReqNo && placementHeaderData?.technologyVendorName ? ' | ' : '') +
        (orderData?.webLink
          ? `<a href="${orderData.webLink}">${placementHeaderData?.technologyVendorName ?? ''}</a>`
          : placementHeaderData?.technologyVendorName ?? ''),
    },
    { name: 'Candidate Name', input: '{{candidate_name}}', value: `${candidateName}` },
    {
      name: 'Candidate Link',
      input: '{{candidate_url}}',
      value: `${baseUrl}/candidate/${placementHeaderData?.candidate?.id}/${placementHeaderData?.brandId}`,
    },
    { name: 'Recruiter', input: '{{recruiter}}', value: `${recruiterName || ''}` },
    { name: 'Recruiter Mail', input: '{{recruiter_mail}}', value: `${!!recruiterEmail ? recruiterEmail : ''}` },
    { name: 'Account Manager', input: '{{account_manager}}', value: `${accountManagerName}` },
    {
      name: 'Account Manager Mail',
      input: '{{account_manager_mail}}',
      value: `${!!accountManagerEmail ? accountManagerEmail : ''}`,
    },
    { name: 'Facility Name', input: '{{facility_name}}', value: `${placementHeaderData?.facility?.name || ''}` },
    {
      name: 'Facility Link',
      input: '{{facility_url}}',
      value: `${baseUrl}/facility/${orderData?.facility?.facilityId}`,
    },
    { name: 'Unit', input: '{{unit_name}}', value: `${orderData?.unit?.name}` },
    {
      name: 'Unit Link',
      input: '{{unit_url}}',
      value: `${baseUrl}/facility/${orderData?.facility?.facilityId}/unit/${orderData?.unit?.unitId}`,
    },
    { name: 'Location', input: '{{location}}', value: formattedAddress },
    {
      name: 'Placement Dates',
      input: '{{placement_dates}}',
      value: placementDates,
    },
    { name: 'Placement Skill Set', input: '{{placement_skillset}}', value: `${formattedSkillsets}` },
    { name: 'Last Confirmation Sent', input: '{{last_confirmation_sent}}', value: `${formattedLatestSentDate}` },
    {
      name: 'Booking Period Dates',
      input: '{{booking_period_dates}}',
      value: bookingPeriod,
    },
    { name: 'Extension Placement Copy', input: '{{extension_placement_copy}}', value: `${extension}` },
    { name: 'TLA', input: '{{tla}}', value: `${headerData?.tla ? 'Yes' : 'No'}` },
  ];

  substitutionData.forEach(substitution => substitutions.push(substitution));
  return substitutionData;
};
